/**
 * The writer's query as a pattern (decision 41; part 19a log, table Q). Pure: the pattern is only built and checked
 * here; it runs in the find worker (principle 4), never on the page.
 */

/** Q7: the longest query or replacement, in UTF-16 units. */
export const MAX_QUERY_LENGTH = 1_000;

/** What the writer asks for. */
export interface FindQuery {
  /** The words or the pattern, as typed. */
  readonly text: string;
  /** Q4: whether upper and lower case must be as typed. */
  readonly matchCase: boolean;
  /** Q5: whether a match must be a whole word (or words). */
  readonly wholeWords: boolean;
  /** Q2, Q3: whether `text` is a regular expression rather than literal text. */
  readonly regex: boolean;
}

/** Why a query gives no pattern: Q7 (`too-long`), Q3 (`invalid`, the engine refused the expression). */
export type QueryProblem = 'too-long' | 'invalid';

/**
 * A query's pattern, or why there is none: `empty` (Q1) or refused (Q3, Q7). The engine's own message isn't kept: it is
 * in the browser's language, not the interface's, and differs between engines.
 */
export type Pattern =
  | { readonly kind: 'pattern'; readonly regex: RegExp }
  | { readonly kind: 'empty' }
  | { readonly kind: 'refused'; readonly problem: QueryProblem };

/** Q5: a character that continues a word: a letter, a combining mark, a digit or a connector such as `_`. */
const WORD_CHARACTER = String.raw`[\p{L}\p{M}\p{N}\p{Pc}]`;

/** Q2: every character with a meaning in a Unicode-mode expression outside a class. */
const SYNTAX = /[\\^$.*+?()[\]{}|/]/gu;

/** Q2: `text` as an expression that matches it literally. */
export function literalPattern(text: string): string {
  return text.replace(SYNTAX, String.raw`\$&`);
}

/**
 * The expression for `query` (table Q): global, Unicode mode (Q3), lines anchored by `^` and `$` (Q6), case folded
 * unless `matchCase` (Q4), kept off word characters on both sides when `wholeWords` (Q5). An expression is checked on
 * its own before it is wrapped, so wrapping never makes a broken one valid (`a)(b` inside `(?:…)`).
 */
export function findPattern(query: FindQuery): Pattern {
  if (query.text === '') return { kind: 'empty' };
  if (query.text.length > MAX_QUERY_LENGTH) return { kind: 'refused', problem: 'too-long' };
  const flags = query.matchCase ? 'gmu' : 'gimu';
  const source = query.regex ? query.text : literalPattern(query.text);
  let alone: RegExp;
  try {
    // Q3: the writer's expression alone, as the engine reads it.
    alone = new RegExp(source, flags);
  } catch {
    return { kind: 'refused', problem: 'invalid' };
  }
  return { kind: 'pattern', regex: query.wholeWords ? new RegExp(`(?<!${WORD_CHARACTER})(?:${source})(?!${WORD_CHARACTER})`, flags) : alone };
}
